/* eslint-disable no-duplicate-imports */
import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { type FrequencyModelDto } from '@treasury/api/channel';
import { Holiday, HolidaysService } from '@treasury/domain/dates';
import { EntitlementsService } from '@treasury/domain/entitlements';
import { TmApiError } from '@treasury/domain/shared';
import {
    CreditorsService,
    defaultFrequency,
    Wire,
    WireAccount,
    WireBeneficiary,
    WireCompany,
    WireConfiguration,
    WireCreditor,
    type WireIsoLabels,
    WireIsoService,
    WiresService,
    WireTemplate,
    WireType,
} from '@treasury/domain/wires';
import { LabeledList, TmContainer } from '@treasury/presentation';
import {
    buildingIcon,
    circleCheckmarkIcon,
    circleWarningIcon,
} from '@treasury/presentation/assets/icons';
import '@treasury/presentation/components/forms/tm-form';
import { SelectItemVm, TmValueChanged } from '@treasury/presentation/components/forms/tm-form';
import '@treasury/presentation/components/forms/tm-form-row';
import '@treasury/presentation/components/forms/tm-slider';
import '@treasury/presentation/components/tm-currency-field';
import {
    mapFrequencyModelDtoToFrequency,
    mapFrequencyToFrequencyModelDto,
} from '@treasury/presentation/components/tm-frequency.mappings';
import { TmSearchTermChangeEvent } from '@treasury/presentation/components/tm-list-header';
import '@treasury/presentation/components/tm-loader';
import '@treasury/presentation/components/tm-toggle-content';
import { debounce, toTitleCase } from '@treasury/utils';
import { format } from 'date-fns';
import { css, html, nothing } from 'lit';
import { columnBodyRenderer, GridColumnBodyLitRenderer } from 'lit-vaadin-helpers';
import { customElement, property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import {
    DatePickerDate,
    inactiveHolidays,
    inactiveWeekends,
} from '../../../../utilities/disabled-date.functions';
import { filterTableResults } from '../../../../utilities/table-result-filter';
import '../../creditors/partials/creditors-table';
import { ValidWireDateTime } from '../../types/wire-valid-datetime.type';
import './wire-template-readonly';

enum IconState {
    Null,
    Valid,
    Invalid,
}

export const tagName = 'wire-template-editable';
@customElement(tagName)
export class WireTemplateEditable extends TmContainer {
    @property({ attribute: false })
    public wire = new Wire();

    @property({ attribute: false })
    public wireTemplate = new WireTemplate();

    @property({ type: Object })
    public frequency: FrequencyModelDto = defaultFrequency;

    @property({ type: Object })
    public wireConfiguration!: WireConfiguration;

    @property({ attribute: false })
    public action = '';

    @property({ attribute: false })
    public wireType = WireType.Domestic;

    @property({ attribute: false })
    public workflow = '';

    @InjectProperty()
    declare private wireService: WiresService;

    @InjectProperty()
    declare private creditorsService: CreditorsService;

    @InjectProperty()
    declare private readonly entitlementsService: EntitlementsService;

    @InjectProperty()
    declare private holidaysService: HolidaysService;

    @InjectProperty()
    declare private wireIsoService: WireIsoService;

    @state()
    private hasWireIsoFeatureFlag = false;

    @state()
    private creditorAccounts: Array<WireBeneficiary> | Array<WireCreditor> = [];

    @state()
    private wireCompanies: Array<WireCompany> = [];

    @state()
    private filteredWireCompanies: Array<WireCompany> = [];

    @state()
    private debitAccounts: Array<WireAccount> = [];

    @state()
    private filteredDebitAccounts: Array<WireAccount> = [];

    @state()
    private amount: string | undefined;

    @state()
    private holidays: Array<Holiday> = [];

    @state()
    private maxStartDate?: Date;

    @state()
    private validWireDateTime: ValidWireDateTime | undefined;

    @state()
    private wireIsoLabels: WireIsoLabels = WireIsoService.legacyLabels;

    @state()
    private initialTemplateName = '';

    @state()
    private isValidTemplateName = true;

    @state()
    private iconState = IconState.Null;

    private debouncedTemplateName = debounce(() => this.validateTemplateName(), 500);

    private get iconConfig() {
        if (this.wireTemplate.name.length < 3) {
            this.iconState = IconState.Null;
        }
        switch (this.iconState) {
            case IconState.Valid:
                return { icon: circleCheckmarkIcon, color: `var(--content-positive)` };
            case IconState.Invalid:
                return { icon: circleWarningIcon, color: `var(--content-negative)` };
            case IconState.Null:
            default:
                // eslint-disable-next-line no-restricted-syntax
                return { icon: null, color: `` };
        }
    }

    public async firstUpdated() {
        await this.tryFetch(
            async () =>
                Promise.all([
                    this.wireIsoService.getEntitlementWireISO20022(),
                    this.wireIsoService.getLabels(),
                ]),
            ([hasWireIsoFeatureFlag, wireIsoLabels]) => {
                this.hasWireIsoFeatureFlag = hasWireIsoFeatureFlag;
                this.wireIsoLabels = wireIsoLabels;
            }
        );

        if (this.action === 'initiate') {
            await this.tryFetch(
                async () =>
                    Promise.all([
                        this.wireService.checkValidDateTime(),
                        this.holidaysService.getHolidays(),
                        this.wireConfiguration
                            ? this.wireConfiguration
                            : (this.wireService.getWireConfiguration() as unknown as WireConfiguration),
                    ]),
                ([validWireDateTime, holidays, wireConfiguration]) => {
                    this.validWireDateTime = validWireDateTime;
                    this.holidays = holidays;
                    this.wireConfiguration = wireConfiguration;

                    if (
                        this.validWireDateTime.isAfterCutoff ||
                        this.validWireDateTime.isHoliday ||
                        this.validWireDateTime.isWeekend
                    ) {
                        this.frequency.valueDate = format(
                            this.validWireDateTime.nextBusinessDay,
                            'P'
                        );
                    }

                    if (this.wireConfiguration.maximumFutureDays) {
                        this.maxStartDate = new Date();
                        this.maxStartDate.setDate(
                            this.maxStartDate.getDate() +
                                (this.wireConfiguration.maximumFutureDays ?? 0)
                        );
                    }
                }
            );
        }

        if (this.action === 'edit' || this.action === 'create') {
            await this.tryFetch(
                async () =>
                    Promise.all([
                        this.creditorsService.getBeneficiariesByType({
                            status: 'Ready',
                            beneficiaryType: toTitleCase(this.wireType),
                            requireEntitlement: false,
                            applicationNameType: 0,
                        }),
                        this.wireService.getWireCompanies(true),
                    ]),
                ([creditorAccounts, wireCompanies]) => {
                    this.creditorAccounts = (creditorAccounts as WireCreditor[]).filter(
                        c => c.isForeignCurrency === false
                    );
                    this.wireCompanies = wireCompanies;
                    this.filteredWireCompanies = this.wireCompanies;
                    this.initialTemplateName = this.wireTemplate.name;
                }
            );
        }
    }

    protected render() {
        if (this.loading) {
            return html`<tm-blocking-loader></tm-blocking-loader>`;
        }
        return html` <tm-form
            @form-changed=${() => {
                this.updateWire();
            }}
        >
            ${this.action === 'edit' || this.action === 'create'
                ? html`${this.renderWireType()}
                      <tm-form-row label="Template name" required .itemsStart=${true}>
                          <tm-text-field
                              .initial=${this.wireTemplate.name}
                              .invalid=${!this.isValidTemplateName}
                              .maxLength=${30}
                              .helperText=${!this.isValidTemplateName
                                  ? 'Wire template name must be unique.'
                                  : ''}
                              @value-changed=${({ detail }: CustomEvent) => {
                                  if (this.initialTemplateName) {
                                      this.wireTemplate.name = detail.value;
                                      this.debouncedTemplateName();
                                  }
                              }}
                          >
                              <tm-icon
                                  slot="suffix"
                                  .icon=${this.iconConfig.icon}
                                  .color=${this.iconConfig.color}
                              ></tm-icon>
                          </tm-text-field>
                      </tm-form-row>
                      <tm-form-row .label=${this.wireIsoLabels.creditor} required noWrap>
                          <tm-slider
                              header=${`Select ${this.wireIsoLabels.creditor}`}
                              .value=${this.wireTemplate?.beneficiary.name
                                  ? this.wireTemplate.beneficiary.name
                                  : undefined}
                              .placeholder=${`Select ${this.wireIsoLabels.creditor}`}
                          >
                              <creditors-table
                                  .creditors=${this.creditorAccounts}
                                  @select=${({ detail, target }: CustomEvent) => {
                                      if (this.wireTemplate) {
                                          this.wireTemplate.beneficiary = detail.toDto();
                                      }
                                      target?.dispatchEvent(
                                          new CustomEvent('close', { bubbles: true })
                                      );
                                      this.updateWire();
                                      this.requestUpdate();
                                  }}
                              ></creditors-table>
                          </tm-slider>
                      </tm-form-row>
                      <tm-form-row .label=${'Wire company name'} required noWrap>
                          <tm-slider
                              header="Select Wire Company"
                              .value=${this.wireTemplate?.wireCompany?.name}
                              .placeholder=${'Select wire company'}
                          >
                              ${this.renderWireCompanies()}
                          </tm-slider>
                      </tm-form-row>
                      <tm-form-row .label=${'Debit account'} required noWrap>
                          <tm-slider
                              header="Select Debit Account"
                              .value=${this.wireTemplate?.debitAccount?.accountDisplayLabel
                                  ? this.wireTemplate.debitAccount.accountDisplayLabel
                                  : undefined}
                              .placeholder=${'Select debit account'}
                              .disabled=${!this.wireTemplate?.wireCompany}
                          >
                              ${this.renderDebitAccounts()}
                          </tm-slider>
                      </tm-form-row>`
                : nothing}
            ${this.action === 'initiate'
                ? html`<tm-form-row label="Template">
                          <tm-slider
                              header="Template Details"
                              .value=${ifDefined(this.wireTemplate?.name)}
                          >
                              <tm-body .padding=${false}>
                                  <wire-template-readonly
                                      .wireTemplate=${this.wireTemplate}
                                      .creditorDetail=${false}
                                  ></wire-template-readonly>
                              </tm-body>
                          </tm-slider>
                          <tm-labeled-list
                              slot="summary"
                              class="py-4"
                              .list=${new LabeledList(
                                  {
                                      wireCompany: this.wire?.wireCompany
                                          ? this.wire.wireCompany.name
                                          : '',
                                      debitAccount: this.wire?.debitAccount
                                          ? (this.wire.debitAccount.accountDisplayLabel as string)
                                          : '',
                                      type: this.wire?.wireTemplate?.isInternational
                                          ? 'International'
                                          : 'Domestic',
                                      beneficiary: this.wire?.beneficiary?.name
                                          ? this.wire.beneficiary.name
                                          : '',
                                  },
                                  ['wireCompany', 'debitAccount', 'type', 'beneficiary'],
                                  { beneficiary: `${this.wireIsoLabels.creditor}` },
                                  { beneficiary: 'view-creditor-detail' }
                              )}
                          ></tm-labeled-list>
                      </tm-form-row>
                      <tm-form-row label="Amount" required>
                          <tm-currency-field
                              class="max-w-[130px]"
                              .initial=${this.wire?.amount}
                              @value-changed=${(e: TmValueChanged<string>) => {
                                  const { value } = e.detail;
                                  if (!value || !this.wire) {
                                      return;
                                  }

                                  this.amount = value;
                                  this.wire.amount = parseFloat(this.amount);
                              }}
                          ></tm-currency-field>
                      </tm-form-row>
                      ${this.renderFrequency()}`
                : nothing}

            <tm-form-row label="Purpose" required .itemsStart=${true}>
                <tm-text-field
                    .initial=${this.action === 'initiate'
                        ? this.wire.purpose
                        : this.wireTemplate.purpose}
                    .maxLength=${30}
                    .helperText="${`${this.wire.purpose?.length ?? 0}/30`}"
                    @value-changed=${(e: CustomEvent) => {
                        if (this.wire) {
                            this.wire.purpose = e.detail.value;
                            this.wireTemplate.purpose = e.detail.value;
                        }
                    }}
                ></tm-text-field>
            </tm-form-row>

            <tm-toggle-content>
                <tm-form-row label="Additional Information" .itemsStart=${true}>
                    <tm-multi-line-text-input
                        placeholder="Line"
                        .maxLines=${4}
                        .characterLimit=${35}
                        .pattern="${/^[a-zA-Z0-9!"#$%'()+/:;\\<=>?@_`^~\-.&, ]+$/}"
                        .value=${this.wire?.additionalInformation ?? []}
                        @change=${(e: CustomEvent) => {
                            if (this.wire) {
                                this.wire.additionalInformation = e.detail;
                            }
                        }}
                    ></tm-multi-line-text-input>
                </tm-form-row>

                ${this.wireType !== WireType.International || this.hasWireIsoFeatureFlag
                    ? html`
                          <tm-form-row label=${this.wireIsoLabels.endToEndId} noWrap>
                              <tm-text-field
                                  .initial=${this.wire?.referenceBeneficiary}
                                  .maxLength=${16}
                                  .helperText="${`${this.wire?.referenceBeneficiary?.length ?? 0}/16`}"
                                  @value-changed=${(e: CustomEvent) => {
                                      if (this.wire) {
                                          this.wire.referenceBeneficiary = e.detail.value;
                                      }
                                  }}
                              ></tm-text-field>
                          </tm-form-row>
                      `
                    : nothing}
            </tm-toggle-content>
        </tm-form>`;
    }

    private async validateTemplateName() {
        if (this.wireTemplate.name === this.initialTemplateName) {
            this.isValidTemplateName = true;
            this.iconState = IconState.Null;
        } else {
            const response = (await this.wireService.validateTemplateName(
                this.wireTemplate.name
            )) as unknown as { isValid: boolean };
            this.isValidTemplateName = response.isValid;
            this.iconState = this.isValidTemplateName ? IconState.Valid : IconState.Invalid;
            this.updateWire();
        }
    }

    private async getDebitAccounts() {
        this.loading = true;
        const type = this.wireType === WireType.Domestic ? 'createDomestic' : 'createIntl';
        try {
            if (this.wire?.wireCompany) {
                this.debitAccounts = await this.wireService.getDebitAccounts(
                    this.wire.wireCompany.id,
                    type
                );
                this.filteredDebitAccounts = this.debitAccounts;
            }
        } catch (error) {
            this.loading = false;
            this.notificationService.renderError(error as TmApiError);
        } finally {
            this.loading = false;
        }
    }

    private updateWire() {
        this.dispatchEvent(
            new CustomEvent('update', {
                detail: { wire: this.wire, wireTemplate: this.wireTemplate },
            })
        );
    }

    private filterResults(e: CustomEvent, type: string) {
        switch (type) {
            case 'wireCompanies':
                this.filteredWireCompanies = filterTableResults(e, this.wireCompanies, ['name']);
                break;
            case 'debitAccounts':
                this.filteredDebitAccounts = filterTableResults(e, this.debitAccounts, [
                    'accountDisplayLabel',
                    'number',
                    'availableBalance',
                ]);
                break;
            default:
        }
    }

    private renderWireType() {
        const templateTypes = [
            {
                label: 'Domestic',
                value: false,
            },
            {
                label: 'International',
                value: true,
            },
        ];
        const initial = templateTypes.find(t => t.value === this.wireTemplate.isInternational);
        return html`<tm-section class="p-4">
            <tm-segmented-control
                class="py-2"
                .controlTheme=${'secondary'}
                .options=${templateTypes}
                .initial=${initial}
                @value-changed=${({ detail }: TmValueChanged<SelectItemVm<boolean>>) => {
                    const value = detail.value?.value ?? false;
                    this.wireTemplate.isInternational = value;
                }}
            ></tm-segmented-control>
        </tm-section>`;
    }

    private renderWireCompanies() {
        const renderAccount = (item: WireCompany) =>
            html`<div class="flex flex-row items-center">
                <tm-icon
                    .icon=${buildingIcon}
                    width="24px"
                    height="24px"
                    color="var(--tm-color-gray-medium)"
                ></tm-icon>
                <div class="ml-4 text-sm font-medium">${item.name}</div>
            </div>`;
        return html`<tm-section>
            <tm-list-header
                .filterButton=${false}
                @search-term-change=${(e: TmSearchTermChangeEvent) => {
                    this.filterResults(e, 'wireCompanies');
                }}
            ></tm-list-header>
            ${this.loading
                ? html`<tm-loader card></tm-loader>`
                : html`<vaadin-grid
                      .items=${this.filteredWireCompanies}
                      @active-item-changed=${(e: CustomEvent) => {
                          if (!e.detail.value) {
                              return;
                          }
                          if (this.wireTemplate) {
                              this.wireTemplate.wireCompany = e.detail.value.toDto();
                          }
                          this.getDebitAccounts();
                          this.updateWire();
                          e.target?.dispatchEvent(new CustomEvent('close', { bubbles: true }));
                      }}
                      ><vaadin-grid-column
                          ${columnBodyRenderer(
                              renderAccount as GridColumnBodyLitRenderer<WireCompany>
                          )}
                      ></vaadin-grid-column
                  ></vaadin-grid>`}
        </tm-section>`;
    }

    private renderDebitAccounts() {
        const renderNameAndNumber = (item: WireAccount) =>
            html`<div class="flex justify-between">
                <div class="flex flex-col">
                    <div class="text-sm font-medium">${item.accountDisplayLabel}</div>
                    <div class="text-xs text-secondary">${item.number}</div>
                </div>
            </div>`;
        return html`<tm-section>
            <tm-list-header
                .filterButton=${false}
                @search-term-change=${(e: TmSearchTermChangeEvent) => {
                    this.filterResults(e, 'debitAccounts');
                }}
            ></tm-list-header>
            ${this.loading
                ? html`<tm-loader card></tm-loader>`
                : html`<vaadin-grid
                      .items=${this.filteredDebitAccounts}
                      @active-item-changed=${(e: CustomEvent) => {
                          if (!e.detail.value) {
                              return;
                          }
                          if (this.wireTemplate) {
                              this.wireTemplate.debitAccount = e.detail.value.toDto();
                          }
                          e.target?.dispatchEvent(new CustomEvent('close', { bubbles: true }));
                          this.updateWire();
                          this.requestUpdate();
                      }}
                  >
                      <vaadin-grid-column
                          ${columnBodyRenderer(
                              renderNameAndNumber as GridColumnBodyLitRenderer<WireAccount>
                          )}
                      >
                      </vaadin-grid-column>
                  </vaadin-grid>`}
        </tm-section>`;
    }

    private renderFrequency() {
        if (this.wireType === WireType.International) {
            return html`<tm-form-row required noWrap .label=${'Effective date'} class="py-2">
                <div class="flex justify-end">${this.wire?.frequency?.valueDate}</div></tm-form-row
            >`;
        } else {
            return html`<tm-form-frequency
                required
                .loading=${this.loading}
                dateLabel="Frequency"
                .frequency=${this.frequency
                    ? mapFrequencyModelDtoToFrequency(this.frequency)
                    : undefined}
                .isDateDisabled=${(date: DatePickerDate) =>
                    inactiveWeekends(date) || inactiveHolidays(date, this.holidays)}
                .maxStartDate=${this.maxStartDate}
                @select-frequency=${(e: CustomEvent) => {
                    if (this.wire) {
                        this.wire.frequency = e.detail
                            ? (mapFrequencyToFrequencyModelDto(e.detail) as FrequencyModelDto)
                            : e.detail;
                    }
                }}
            ></tm-form-frequency>`;
        }
    }

    public static get styles() {
        return [
            css`
                :host {
                    display: block;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: WireTemplateEditable;
    }
}
